import { cn } from '@client/utils/styling/cn';
import { Spin, type SpinProps } from 'antd';
import type { ReactNode } from 'react';
import { forwardRef } from 'react';

type LoadingSize = 'small' | 'medium' | 'large';

export interface LoadingProps extends Omit<SpinProps, 'size' | 'children'> {
    /** Spinner size. small ≈ 16px, medium ≈ 24px, large ≈ 32px */
    size?: LoadingSize;
    /** When provided, renders in overlay mode — spinner appears over children. */
    children?: ReactNode;
}

/**
 * Unified loading spinner wrapper for Ant Design `Spin`.
 *
 * Two modes:
 * - **Standalone** (no children): renders a centred spinner filling the parent's height.
 * - **Overlay** (with children): wraps content with a spinner overlay.
 *
 * When `spinning` is explicitly `false` in overlay mode, children are rendered
 * directly without any Spin wrapper DOM.
 *
 * Extends `SpinProps` — all native props (`indicator`, `delay`, `fullscreen`, etc.)
 * pass through to Ant Design `Spin`.
 *
 * Note: The app globally sets `Spin.setDefaultIndicator(<PuffLoader>)` in
 * `useBodyClass.tsx`, so Loading renders PuffLoader, not Ant Design's default dots.
 */
export const Loading = forwardRef<HTMLDivElement, LoadingProps>(
    ({ size = 'medium', children, className, classNames, description, ...spinProps }, ref) => {
        // Overlay mode: wrap children with Spin
        if (children !== undefined) {
            // When explicitly not spinning, render children directly (no wrapper DOM).
            // This preserves the useSpin contract where idle = no wrapper.
            if (spinProps.spinning === false) {
                return <>{children}</>;
            }
            return (
                <Spin
                    spinning={spinProps.spinning ?? true}
                    size={size}
                    className={className}
                    classNames={classNames}
                    description={description}
                    {...spinProps}
                >
                    {children}
                </Spin>
            );
        }

        // Standalone mode: centred spinner. Childless (non-nested) Spin so the
        // description keeps its natural width and stays inside Spin's aria-live
        // region — the old empty-child nesting constrained the description to
        // the empty content's width, wrapping it into an unreadable column.
        const standaloneClassNames: SpinProps['classNames'] =
            typeof classNames === 'function'
                ? (info) => {
                      const resolvedClassNames = classNames(info);
                      return {
                          ...resolvedClassNames,
                          description: cn('mt-3 text-center text-muted-foreground', resolvedClassNames?.description),
                      };
                  }
                : {
                      ...classNames,
                      description: cn('mt-3 text-center text-muted-foreground', classNames?.description),
                  };

        return (
            <div ref={ref} className={cn('flex h-full items-center justify-center', className)}>
                <Spin spinning size={size} description={description} classNames={standaloneClassNames} {...spinProps} />
            </div>
        );
    },
);

Loading.displayName = 'Loading';

export type { LoadingSize };
